import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getUnidad } from '../../api/client'
import Conversacion from './Conversacion'
import HistorialLista from './HistorialLista'
import PartesDeUnidad from '../partes/PartesDeUnidad'
import { formatFecha } from './tipos'

// Side panel for one unit, opened from its code in the Tabla or the Planilla:
// its conversation, its partes (each opens in the Partes section) and its
// change history.
export default function UnidadPanel({ unidadId, estados, estadoLabel, onClose }) {
  const { t } = useTranslation('engineering')
  const [unidad, setUnidad] = useState(null)
  const [tab, setTab] = useState('conversacion')
  const [error, setError] = useState('')

  useEffect(() => {
    let vivo = true
    getUnidad(unidadId)
      .then(u => { if (vivo) { setUnidad(u); setError('') } })
      .catch(() => { if (vivo) setError(t('unidades.panel.notFound')) })
    return () => { vivo = false }
  }, [unidadId, t])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const estado = estados.find(e => e.value === unidad?.estado_actual)

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{unidad?.codigo || '…'}</h2>
              {unidad && (
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  {unidad.proyecto && <span>{unidad.proyecto}</span>}
                  <span title={t('unidades.fechaIngreso')}>· {unidad.fecha_ingreso
                    ? t('unidades.ingresoEl', { fecha: formatFecha(unidad.fecha_ingreso) }) : t('unidades.sinIngreso')}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${estado?.cls || 'bg-gray-100 text-gray-600'}`}>
                    {estadoLabel(unidad.estado_actual)}
                  </span>
                </div>
              )}
            </div>
            <button type="button" onClick={onClose} title={t('unidades.cancel')}
              className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
          </div>
          <div className="flex gap-1 mt-3">
            {/* Ideal buses are no vehicles: no partes to list. */}
            {(unidad?.es_ideal ? ['conversacion', 'historial'] : ['conversacion', 'partes', 'historial']).map(k => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}>
                {t(`unidades.panel.${k}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 px-5 py-4 overflow-hidden">
          {error ? <p className="text-red-500 text-sm">{error}</p>
            : tab === 'conversacion' ? <Conversacion unidadId={unidadId} />
            : tab === 'partes' ? <div className="h-full overflow-y-auto"><PartesDeUnidad unidadId={unidadId} /></div>
            : <div className="h-full overflow-y-auto"><HistorialLista unidadId={unidadId} /></div>}
        </div>
      </aside>
    </div>
  )
}
